import { api } from '@setplex/tria-api'
import { combine, createEvent, createStore, restore, sample } from 'effector'
import { and, combineEvents, not, or } from 'patronum'
import { options } from '~/core/flags'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { model as bannersModel } from '~/entities/banners'
import { model as moviesAllCategoryModel } from '~/entities/movies/all-category'
import { model as movieBundleModel } from '~/entities/movies/bundles'
import { model as movieCategoriesModel } from '~/entities/movies/categories'
import { model as moviesFeaturedModel } from '~/entities/movies/featured'
import { entitiesPlayerTrailerModel } from '~/entities/player/trailer'
import '~/widgets/Banners/BannerRowContainerWithTrailerPortal'
import * as pinLockFeature from '~/features/pin-lock'
import '~/shared/ui/translations/general-translations'
import { genPagesLogic } from '~/shared/effector'
import { noop } from '~/shared/helpers'
import { $moviesTitles } from './translations'

const DEFAULT_ROW_ELEMENTS_COUNT = 36
const DEFAULT_ROWS_COUNT = 9
// *
// * events
// *
export const init = createEvent<{ query: URLSearchParams }>()
export const left = createEvent()

export const load = createEvent<{
  page: number
  count?: number
}>()

// Reset pin-lock when slide changes in banner carousel
sample({
  clock: entitiesPlayerTrailerModel.onBannerSlideChanged,
  target: pinLockFeature.resetUnlockId,
})

export const seeAllMovieClick = createEvent<{ id: number }>()
sample({
  clock: seeAllMovieClick,
  fn: ({ id }) => `/content/movie/categories/${id}`,
  target: router.navigatePush,
})

export const seeAllMovieCategoryClick = createEvent<{ id: number }>()
sample({
  clock: seeAllMovieCategoryClick,
  fn: ({ id }) => `/content/movie/categories/${id}/subcategories`,
  target: router.navigatePush,
})

interface IRedirectToMoviePlayerParams {
  id?: number
  categoryId?: number
}

export const redirectToMoviePlayer = createEvent<IRedirectToMoviePlayerParams>()

/** Skeleton start*/
export const setContentLoading = createEvent<boolean>()

export const $contentLoading = restore(setContentLoading, false)

/** Skeleton end*/

sample({
  clock: redirectToMoviePlayer,
  fn: ({ id, categoryId }) =>
    `/player/movie?vodId=${id}&categoryId=${categoryId}`,
  target: router.navigatePush,
})

// *
// * CONNECTIONS
// *

sample({
  clock: init,
  fn: ({ query }) => ({
    page: Number(query.get('page')) || 0,
    count: Number(query.get('count')) || DEFAULT_ROWS_COUNT,
  }),
  target: load,
})

const loadMoreCategoriesWithVods = genPagesLogic(
  api.vods.$pageInfoCategoriesWithVods,
  api.vods.loadCategoriesWithVodsFx
)

sample({
  clock: load,
  filter: not(api.vods.initCategoriesWithVodsFx.pending),
  target: api.vods.initCategoriesWithVodsFx,
})
sample({
  clock: load,
  filter: not(api.banners.getBannersFx.pending),
  fn: noop,
  target: api.banners.getBannersFx,
})
sample({
  clock: load,
  filter: not(api.banners.getFeaturedCarouselFx.pending),
  fn: noop,
  target: api.banners.getFeaturedCarouselFx,
})
sample({
  clock: load,
  filter: not(api.vods.getLastAddedMoviesFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.vods.getLastAddedMoviesFx,
})
sample({
  clock: load,
  filter: not(api.vodBundles.initVodBundlesRowByPageFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.vodBundles.initVodBundlesRowByPageFx,
})
sample({
  clock: load,
  filter: not(api.vods.initInfiniteVodsCategoryFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.vods.initInfiniteVodsCategoryFx,
})

// *
// * stores
// *
/********** Skeleton logics starts ***********/
const $isAnyInitFxPending = or(
  // TODO attach fx`s needed
  api.banners.getBannersFx.pending,
  api.banners.getFeaturedCarouselFx.pending,
  api.vods.initCategoriesWithVodsFx.pending,
  api.vodBundles.initVodBundlesRowByPageFx.pending,
  api.vods.initInfiniteVodsCategoryFx.pending
)

sample({
  clock: $isAnyInitFxPending,
  source: { pathname: router.$pathname },
  filter: ({ pathname }) => pathname === '/modules/movie',
  fn: (_, clockData) => clockData,
  target: setContentLoading,
})
/********** Skeleton logics ends ***********/

const moviesCatalogInitsSettled = combineEvents({
  events: {
    banners: api.banners.getBannersFx.finally,
    featured: api.banners.getFeaturedCarouselFx.finally,
    categories: api.vods.initCategoriesWithVodsFx.finally,
    bundles: api.vodBundles.initVodBundlesRowByPageFx.finally,
    infinite: api.vods.initInfiniteVodsCategoryFx.finally,
  },
})

export const $moviesCatalogSettled = createStore(false).reset(init)

sample({
  clock: moviesCatalogInitsSettled,
  fn: () => true,
  target: $moviesCatalogSettled,
})

export const $bannerVodCarouselShown = and(
  options.$bannerCarouselsEnabled,
  bannersModel.$bannersTypeVodFormatted.map((banners) =>
    banners.some(({ bannerBundles }) => bannerBundles?.length)
  )
)

export const $bannerCarousels = bannersModel.$bannersTypeVodFormatted

const $featuredVodCarouselShown = and(
  options.$featuredCarouselsEnabled,
  moviesFeaturedModel.$featuredCarouselTypeVodFormatted.map((carousel) =>
    carousel?.some(({ enabledOnModulePage }) => enabledOnModulePage)
  )
)

const $anyVisibleMoviesShelf = combine(
  $bannerVodCarouselShown,
  $featuredVodCarouselShown,
  movieBundleModel.$vodBundlesRowFormatted,
  movieCategoriesModel.$categoriesWithVodsFormattedVods,
  api.vods.$pageInfoCategoriesWithVods,
  moviesAllCategoryModel.$infiniteVodsFormatted,
  (banners, featured, bundles, categories, pageInfo, infinite) =>
    banners ||
    featured ||
    bundles.length > 0 ||
    (categories?.length ?? 0) > 0 ||
    Boolean(pageInfo?.last && infinite?.content?.length)
)

export const $isMoviesEmptyPage = combine(
  $moviesCatalogSettled,
  $anyVisibleMoviesShelf,
  (settled, anyVisible) => settled && !anyVisible
)

// clear paginatable stores
api.banners.$bannersTypeVod.reset(init)
api.vodBundles.$vodBundlesRow.reset(init)
api.vods.$categoriesWithVods.reset(init) // NOT necessary anymore, coz init event refills store (removing would show prev data on page init)

register(seeAllMovieClick, 'seeAllMovieClick')
register(seeAllMovieCategoryClick, 'seeAllMovieCategoryClick')
register(
  moviesFeaturedModel.$featuredCarouselTypeVodFormatted,
  '$featuredCarouselTypeVod'
)
register(
  moviesFeaturedModel.$featuredCarouselVodTitle,
  '$featuredCarouselVodTitle'
)
register(movieBundleModel.$vodBundlesRowFormatted, '$vodBundlesRow')
register(movieBundleModel.$vodBundlesRowTitle, '$vodBundlesRowTitle')
register(
  movieCategoriesModel.$categoriesWithVodsFormattedVods,
  '$categoriesWithVodsFormattedVods'
) // api.vods.$categoriesWithVods
register($featuredVodCarouselShown, '$featuredVodCarouselShown')
register($bannerVodCarouselShown, '$bannerVodCarouselShown')
register(loadMoreCategoriesWithVods, 'loadMoreCategoriesWithVods')
register(api.vods.$pageInfoCategoriesWithVods, '$pageInfoCategoriesWithVods')
register(
  moviesAllCategoryModel.$infiniteVodsFormatted,
  '$infiniteVodsFormatted'
)
register(moviesAllCategoryModel.$infiniteVodsTitle, '$infiniteVodsTitle')

register($moviesTitles, '$moviesTitles')
register($isMoviesEmptyPage, '$isMoviesEmptyPage')
